import { membershipDay } from "./membership.ts";
import { localDate, shiftDateKey } from "./time.ts";

/**
 * When a sold package runs (#ai-feedback 2026-10-26, card #1). A package is not tied to the
 * calendar month: it runs `validityDays` days from its first day.
 *
 * - No package runs for the same person and kind: the first day is the day of the sale.
 * - A package runs: the new one waits, and starts when the running one ends. The running one ends
 *   on its end date, or after its last booked session when it has no session left. While the new
 *   package is not used, its dates move with the running one (a booking or a cancel).
 * - The owner can start the new package today instead.
 *
 * The server rule: `packages/core/src/services/package-queue.ts`.
 */

/** The last day of a period of `days` days from `startsOn`: the day before the next period starts. */
export function lastDayOf(startsOn: string, days: number): string {
  return shiftDateKey(startsOn, days - 1);
}

/**
 * The last instant a package pays for. `expires_at` is the first instant the package does not pay
 * for: 00:00 after the last day since card #1, the time of the sale or 23:59 of the last day for
 * older packages and starting balances. So the last day is the day of the instant just before it.
 * Show or compare this instant, never `expires_at` itself, as the day a package ends.
 */
export function packageLastInstant(expiresAt: Date | string): Date {
  return new Date(new Date(expiresAt).getTime() - 1);
}

/** The last day of a package (`yyyy-MM-dd`), in the business timezone. See `packageLastInstant`. */
export function packageLastDay(expiresAt: Date | string, timeZone: string): string {
  return localDate(packageLastInstant(expiresAt), timeZone);
}

/** "8 sessions, from 16 Nov to 15 Dec" (both days are in the period). */
export function packPeriodLine(input: { credits: number; unlimited?: boolean | null; startsOn: string; endsOn: string }): string {
  const what = input.unlimited ? "Unlimited sessions" : `${input.credits} ${input.credits === 1 ? "session" : "sessions"}`;
  return `${what}, from ${membershipDay(input.startsOn)} to ${membershipDay(input.endsOn)}`;
}
